import { put, takeLatest, call, delay } from 'redux-saga/effects';
import {
  createFolder,
  createFolderFailure,
  createFolderSuccess,
  createStep,
  createStepFailure,
  createStepSuccess,
  createTreatmentPlan,
  createTreatmentPlanFailure,
  createTreatmentPlanSuccess,
  fetchStepsTree,
  fetchStepsTreeFailure,
  fetchStepsTreeSuccess,
  fetchTreatmentPlanById,
  fetchTreatmentPlanByIdFailure,
  fetchTreatmentPlanByIdSuccess,
  fetchTreatmentPlansTree,
  fetchTreatmentPlansTreeFailure,
  fetchTreatmentPlansTreeSuccess,
  saveTreatmentPlan,
  saveTreatmentPlanFailure,
  saveTreatmentPlanSuccess,
  sendNotification,
  sendNotificationSuccess,
  sendNotificationFailure,
  setServerTreatmentPlan,
  setTreatmentPlanId,
  setTreatmentPlanSteps,
  setSelectedStepId,
  loadTemplate,
  saveAsDraftFailure,
  saveAsDraftSuccess,
  updateDraftSuccess,
  updateDraftFailure,
} from '../slices/treatmentPlan';
import {
  CreateFolderRequest,
  CreateStepDto,
  CreateTreatmentPlanRequest,
  Folder,
  Step,
  StepsHierarchy,
  TreatmentPlanAPI,
  TreatmentPlanFolder,
  TreatmentPlanResponse,
  DeliveryMethod,
  SendNotificationRequest,
  transcribeAudioService,
  fetchTranscriptionJob,
  transcribeToPlanService,
} from '../../api/services/treatmentPlan';
import { ApiResponse } from '../../utils/ApiService';
import { PayloadAction } from '@reduxjs/toolkit';
import { TreatmentPlan } from '../../types/treatmentPlan';
import { select } from 'redux-saga/effects';
import { RootState } from '../store';
import { createDefaultStep } from '../../components/treatment-plan/defaultStep';
import { ServerTreatmentPlanStep } from '../../api/types/treatmentPlanStep';
import {
  transcribeAudio as transcribeAudioAction,
  transcribeAudioSuccess,
  transcribeAudioFailure,
  setTranscript,
  transcribeToPlan as transcribeToPlanAction,
  transcribeToPlanSuccess,
  transcribeToPlanFailure,
} from '../slices/treatmentPlan';
import { getErrorMessage } from '../../utils/errorParser';

function* fetchStepsTreeSaga() {
  try {
    const response: ApiResponse<StepsHierarchy> = yield call(TreatmentPlanAPI.getReusableSteps);
    if (response.success && response.data) {
      const data = response.data;
      const sortedChildren = Array.isArray(data.children)
        ? [...data.children].sort(
            (a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime()
          )
        : data.children;
      const sortedData = { ...data, children: sortedChildren } as StepsHierarchy;
      yield put(fetchStepsTreeSuccess(sortedData));
    } else {
      yield put(fetchStepsTreeFailure('Failed to fetch steps tree'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(fetchStepsTreeFailure(errorMessage));
  }
}

function* createStepSaga(action: PayloadAction<CreateStepDto>) {
  try {
    const response: ApiResponse<Step> = yield call(
      TreatmentPlanAPI.createReusableStep,
      action.payload
    );
    if (response.success && response.data) {
      yield put(createStepSuccess());
      yield put(fetchStepsTree());
    } else {
      yield put(createStepFailure('Failed to create step'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(createStepFailure(errorMessage));
  }
}

function* createFolderSaga(
  action: PayloadAction<{ request: CreateFolderRequest; fetchTree: 'steps' | 'treatmentPlans' }>
) {
  try {
    const response: ApiResponse<Folder> = yield call(
      TreatmentPlanAPI.createFolder,
      action.payload.request
    );
    if (response.success && response.data) {
      yield put(createFolderSuccess());
      if (action.payload.fetchTree === 'steps') {
        yield put(fetchStepsTree());
      } else {
        yield put(fetchTreatmentPlansTree());
      }
    } else {
      yield put(createFolderFailure('Failed to create folder'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(createFolderFailure(errorMessage));
  }
}

function* fetchTreatmentPlansTreeSaga() {
  try {
    const response: ApiResponse<TreatmentPlanFolder> = yield call(
      TreatmentPlanAPI.getTreatmentPlans
    );
    if (response.success && response.data) {
      yield put(fetchTreatmentPlansTreeSuccess(response.data));
    } else {
      yield put(fetchTreatmentPlansTreeFailure('Failed to fetch treatment plans tree'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(fetchTreatmentPlansTreeFailure(errorMessage));
  }
}

function* createTreatmentPlanSaga(action: PayloadAction<CreateTreatmentPlanRequest>) {
  try {
    const response: ApiResponse<TreatmentPlan> = yield call(
      TreatmentPlanAPI.createTreatmentPlan,
      action.payload
    );
    if (response.success && response.data) {
      yield put(createTreatmentPlanSuccess());
      yield put(fetchTreatmentPlansTree());
    } else {
      yield put(createTreatmentPlanFailure('Failed to create treatment plan'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(createTreatmentPlanFailure(errorMessage));
  }
}

function* fetchTreatmentPlanByIdSaga(action: PayloadAction<number>) {
  try {
    const planId = action.payload;

    // Fetch all plans and filter by ID since there's no direct get-by-id API
    const plansResponse: ApiResponse<TreatmentPlanResponse[]> = yield call(
      TreatmentPlanAPI.getTreatmentPlans
    );

    if (!plansResponse.success || !plansResponse.data) {
      yield put(fetchTreatmentPlanByIdFailure('Failed to fetch treatment plans'));
      return;
    }

    // Find the specific plan by ID
    const planData = Array.isArray(plansResponse.data)
      ? plansResponse.data.find(
          plan => plan && typeof plan === 'object' && 'id' in plan && plan.id === planId
        )
      : (plansResponse.data as TreatmentPlanResponse).id === planId
        ? plansResponse.data
        : null;

    if (!planData) {
      yield put(fetchTreatmentPlanByIdFailure(`Treatment plan with ID ${planId} not found`));
      return;
    }

    yield put(setServerTreatmentPlan(planData));

    // Dispatch success action with the plan data
    yield put(fetchTreatmentPlanByIdSuccess());
  } catch (error: any) {
    console.error('Error fetching treatment plan:', error);
    const errorMessage = getErrorMessage(error);
    yield put(fetchTreatmentPlanByIdFailure(errorMessage));
  }
}

const TEMPORARY_ID_THRESHOLD = 1_000_000_000;
const isTemporaryStepId = (id: number | null | undefined): boolean =>
  id != null && id >= TEMPORARY_ID_THRESHOLD;

function cleanSteps(steps: ServerTreatmentPlanStep[]) {
  const stepsWithoutIds = steps.map(step => {
    const isTemporaryId = step.is_temporary;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { step_num, is_temporary, media_list, ...stepData } = step;
    const mediaIds = media_list ?? (stepData.media ? [Number(stepData.media)] : []);
    const normalizedMedia = mediaIds.length ? mediaIds[0] : null;
    const stepPayload = { ...stepData, media: normalizedMedia, media_list: mediaIds };

    // Null out relative_to_step if it references a temporary (unsaved) step
    if (isTemporaryStepId(stepPayload.relative_to_step as number | null | undefined)) {
      stepPayload.relative_to_step = null;
    }

    // If ID is temporary, remove it
    const dataWithCorrectId = isTemporaryId ? { ...stepPayload, id: undefined } : stepPayload;

    return { ...dataWithCorrectId, complete: false };
  });
  return stepsWithoutIds;
}

function* saveTreatmentPlanSaga() {
  try {
    const state: RootState = yield select();
    const { currentTreatmentPlan, sentToPatientsIds } = state.treatmentPlan;
    const originalSteps = currentTreatmentPlan.steps;

    if (!currentTreatmentPlan.id) {
      // CRITICAL FIX: Preserve existing steps instead of creating just one default step
      // This prevents the bug where all user-created tasks disappear when saveTreatmentPlan
      // is called during patient selection or other state changes
      const stepsToSave =
        currentTreatmentPlan.steps.length > 0 ? currentTreatmentPlan.steps : [createDefaultStep(1)];

      // Create new plan
      const createPlanRequest = {
        name: currentTreatmentPlan.name,
        description: '',
        tags: [],
        assign_patients: sentToPatientsIds.map(id => parseInt(id, 10)),
        steps: cleanSteps(stepsToSave), // Use existing steps or default step
        is_template: sentToPatientsIds.length === 0 && currentTreatmentPlan.isTemplateLoaded,
      };

      const response: ApiResponse<TreatmentPlanResponse[]> = yield call(
        TreatmentPlanAPI.createTreatmentPlan,
        createPlanRequest
      );

      if (!response.success || !response.data) {
        throw new Error('Failed to create treatment plan');
      }

      // Get the new plan ID
      const newPlanData = Array.isArray(response.data) ? response.data[0] : response.data;
      const newPlanId = newPlanData.id;
      yield put(setServerTreatmentPlan(newPlanData));
      if (newPlanId) {
        yield put(setTreatmentPlanId(newPlanId));
      }
      const steps = newPlanData.steps;
      if (steps && steps.length > 0) {
        const idMapping = new Map<number, number>();

        // Create a new array of steps with the server IDs
        const updatedSteps = steps.map((step, index) => {
          const localStep = originalSteps[index];
          if (localStep?.is_temporary && localStep.id) {
            idMapping.set(localStep.id as number, step.id as number);
          }
          const localMediaList =
            localStep?.media_list ?? (localStep?.media ? [Number(localStep.media)] : []);
          const serverMediaList =
            (step as any).media_list ?? (step.media ? [Number(step.media)] : localMediaList);

          return {
            ...step,
            id: step.id,
            is_temporary: false,
            media_list: serverMediaList,
            media: serverMediaList.length ? serverMediaList[0] : null,
          };
        });

        const remappedSteps = updatedSteps.map((step, index) => {
          const localStep = originalSteps[index];
          const relativeToStep = localStep?.relative_to_step;

          if (relativeToStep && idMapping.has(relativeToStep as number)) {
            return { ...step, relative_to_step: idMapping.get(relativeToStep as number) };
          }

          return step;
        });

        // Update the steps in Redux
        yield put(setTreatmentPlanSteps(remappedSteps));

        const { selectedStepId } = currentTreatmentPlan;
        if (selectedStepId !== null && idMapping.has(selectedStepId)) {
          const newStepId = idMapping.get(selectedStepId);
          yield put(setSelectedStepId(newStepId as number));
        }
      }
      // Only set selected step if there are steps available
      else if (newPlanData.steps && newPlanData.steps.length > 0 && newPlanData.steps[0]?.id) {
        yield put(setSelectedStepId(newPlanData.steps[0].id as number));
      }
      yield put(saveTreatmentPlanSuccess(new Date().toISOString()));
    } else {
      // Update existing plan

      const updatePlanRequest = {
        name: currentTreatmentPlan.name,
        tags: [],
        steps: cleanSteps(currentTreatmentPlan.steps),
        assign_patients: sentToPatientsIds.map(id => parseInt(id, 10)),
      };

      const response: ApiResponse<TreatmentPlanResponse> = yield call(
        TreatmentPlanAPI.updateTreatmentPlan,
        {
          id: currentTreatmentPlan.id,
          ...updatePlanRequest,
        }
      );

      if (response.success && response.data) {
        // Get the returned plan with updated steps that have server-assigned IDs
        const responseData = response.data;

        // If the response includes steps with IDs, update our local steps with these IDs
        if (responseData.steps && responseData.steps.length > 0) {
          // Create a mapping of temporary IDs to server IDs
          const idMapping = new Map<number, number>();

          // Create a new array of steps with the server IDs
          const updatedSteps = currentTreatmentPlan.steps.map((step, index) => {
            // If this step has a corresponding server step (based on position)
            if (responseData.steps && index < responseData.steps.length) {
              const serverStep = responseData.steps[index];

              // Store the mapping of temporary to server ID if this is a temporary step
              if (step.is_temporary && step.id) {
                idMapping.set(step.id as number, serverStep.id as number);
              }

              const localMediaList = step.media_list ?? (step.media ? [Number(step.media)] : []);
              const serverMediaList =
                (serverStep as any).media_list ??
                (serverStep.media ? [Number(serverStep.media)] : localMediaList);

              // Return the step with its server-assigned ID
              return {
                ...step,
                id: serverStep.id,
                is_temporary: false, // Mark as no longer temporary since it's saved on server
                media_list: serverMediaList,
                media: serverMediaList.length ? serverMediaList[0] : null,
              };
            }
            return step;
          });

          // Remap any relative_to_step references from temporary IDs to server IDs
          const remappedSteps = updatedSteps.map(step => {
            if (step.relative_to_step && idMapping.has(step.relative_to_step as number)) {
              return { ...step, relative_to_step: idMapping.get(step.relative_to_step as number) };
            }
            return step;
          });

          // Update the local treatment plan with steps that have server IDs
          yield put(setTreatmentPlanSteps(remappedSteps));

          // If the currently selected step was a temporary one, update its ID
          const { selectedStepId } = currentTreatmentPlan;
          if (selectedStepId !== null && idMapping.has(selectedStepId)) {
            const newStepId = idMapping.get(selectedStepId);
            yield put(setSelectedStepId(newStepId as number));
          }
        }

        // Update the server plan data in the store
        yield put(setServerTreatmentPlan(responseData));
        yield put(saveTreatmentPlanSuccess(new Date().toISOString()));
      } else {
        throw new Error('Failed to update treatment plan');
      }
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(saveTreatmentPlanFailure(errorMessage));
  }
}

function* sendNotificationSaga(
  action: PayloadAction<{
    treatmentPlanId: number;
    patientIds: number[];
    deliveryMethod: DeliveryMethod;
  }>
) {
  try {
    const { treatmentPlanId, patientIds, deliveryMethod } = action.payload;
    const state: RootState = yield select();
    const { isDraftLoaded, draftId } = state.treatmentPlan.currentTreatmentPlan;

    if (isDraftLoaded && draftId) {
      // This is a draft - first save it as a regular treatment plan, then send notification
      // Save the draft as a regular treatment plan
      yield call(saveTreatmentPlanSaga);

      // Get the updated state after saving
      const updatedState: RootState = yield select();
      const updatedPlanId = updatedState.treatmentPlan.currentTreatmentPlan.id;

      if (updatedPlanId) {
        // Now send notification for the saved treatment plan
        const request: SendNotificationRequest = {
          patient_ids: patientIds,
          delivery_method: deliveryMethod,
        };

        const response: ApiResponse<void> = yield call(
          TreatmentPlanAPI.sendNotification,
          updatedPlanId,
          request
        );

        if (response.success) {
          yield put(sendNotificationSuccess());
        } else {
          yield put(sendNotificationFailure('Failed to send notification'));
        }
      } else {
        yield put(sendNotificationFailure('Failed to save draft as treatment plan'));
      }
    } else {
      // Regular treatment plan - send notification as usual
      const request: SendNotificationRequest = {
        patient_ids: patientIds,
        delivery_method: deliveryMethod,
      };

      const response: ApiResponse<void> = yield call(
        TreatmentPlanAPI.sendNotification,
        treatmentPlanId,
        request
      );

      if (response.success) {
        yield put(sendNotificationSuccess());
      } else {
        yield put(sendNotificationFailure('Failed to send notification'));
      }
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error) || 'Failed to send notification';
    yield put(sendNotificationFailure(errorMessage));
  }
}

function* transcribeAudioSaga(action: PayloadAction<Blob>) {
  try {
    const response: ApiResponse<{ job_id: number; status: string }> = yield call(
      transcribeAudioService,
      action.payload
    );
    if (!response.success || !response.data?.job_id) {
      yield put(transcribeAudioFailure('Transcription failed to start'));
      return;
    }

    const jobId = response.data.job_id;
    const maxAttempts = 30;
    const pollDelayMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      yield delay(pollDelayMs);
      const jobResponse: ApiResponse<{
        status: string;
        transcription?: string;
        error?: string;
      }> = yield call(fetchTranscriptionJob, jobId);

      if (!jobResponse.success || !jobResponse.data) {
        continue;
      }

      const { status, transcription, error } = jobResponse.data;
      const normalizedStatus = String(status || '').toLowerCase();

      if (
        transcription &&
        (normalizedStatus === 'completed' ||
          normalizedStatus === 'success' ||
          normalizedStatus === 'done' ||
          normalizedStatus === 'finished')
      ) {
        yield put(transcribeAudioSuccess(transcription));
        yield put(setTranscript(transcription));
        return;
      }

      if (normalizedStatus === 'failed' || error) {
        yield put(transcribeAudioFailure(error || 'Transcription failed'));
        return;
      }
    }

    yield put(transcribeAudioFailure('Transcription timed out'));
  } catch (error: any) {
    const errorMessage = getErrorMessage(error) || 'Transcription failed';
    yield put(transcribeAudioFailure(errorMessage));
  }
}

function* transcribeToPlanSaga(
  action: PayloadAction<{ transcript: string; break_into_tasks: boolean; add_dates: boolean }>
) {
  try {
    const state: RootState = yield select();
    const { sentToPatientsIds } = state.treatmentPlan;
    const { transcript, break_into_tasks, add_dates } = action.payload;

    // Get patient ID if available
    const patientId = sentToPatientsIds.length > 0 ? parseInt(sentToPatientsIds[0], 10) : undefined;

    const response: ApiResponse<any> = yield call(transcribeToPlanService, transcript, patientId, {
      break_into_tasks,
      add_dates,
    });
    if (response.success && response.data) {
      // Map the response to the template structure
      const detailed = response.data;

      const mappedSteps = (detailed.steps || []).map((step: any) => ({
        id: step.id,
        title: step.title,
        description: step.description,
        start_date: step.start_date,
        end_date: step.end_date,
        start_time: step.start_time || null,
        end_time: step.end_time || null,
        audio: step.audio ? Number(step.audio) : null,
        media: step.media ? Number(step.media) : null,
        media_list: Array.isArray(step.media_list)
          ? step.media_list.map((m: any) => Number(m))
          : step.media
            ? [Number(step.media)]
            : [],
        note: step.note,
        folder: step.folder,
        tags: step.tags || [],
        flag_if_missed: step.flag_if_missed,
        time_specificity: step.time_specificity,
        relative_to_step: step.relative_to_step,
        relative_to_date: step.relative_to_date,
        offset_amount: step.offset_amount,
        offset_units: step.offset_units,
        relative_direction: step.relative_direction,
        urgency: step.urgency,
        step_num: step.step_num,
        complete: step.complete,
        status: step.status,
        is_temporary: false,
      }));
      const completeTemplate = {
        id: detailed.id,
        name: detailed.name,
        type: 'treatment',
        tasks: mappedSteps.length,
        uses: 0,
        date_created: detailed.date_created || new Date().toISOString(),
        description: detailed.description,
        steps: mappedSteps,
      };

      yield put(loadTemplate(completeTemplate));
      yield put(transcribeToPlanSuccess());
    } else {
      yield put(transcribeToPlanFailure('Transcribe to plan failed'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error) || 'Transcribe to plan failed';
    yield put(transcribeToPlanFailure(errorMessage));
  }
}

function* deleteFolderSaga(
  action: PayloadAction<{ id: number; fetchTree: 'steps' | 'treatmentPlans' }>
) {
  try {
    const response: ApiResponse<void> = yield call(
      TreatmentPlanAPI.deleteFolder,
      action.payload.id
    );
    if (response.success) {
      yield put({ type: 'treatmentPlan/deleteFolderSuccess' });
      if (action.payload.fetchTree === 'steps') {
        yield put(fetchStepsTree());
      } else {
        yield put(fetchTreatmentPlansTree());
      }
    } else {
      yield put({ type: 'treatmentPlan/deleteFolderFailure', payload: 'Failed to delete folder' });
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error) || 'Failed to delete folder';
    yield put({ type: 'treatmentPlan/deleteFolderFailure', payload: errorMessage });
  }
}

function* deleteTreatmentPlanSaga(action: PayloadAction<{ id: number }>) {
  try {
    const response: ApiResponse<void> = yield call(
      TreatmentPlanAPI.deleteTreatmentPlan,
      action.payload.id
    );
    if (response.success) {
      yield put({ type: 'treatmentPlan/deleteTreatmentPlanSuccess' });
      yield put(fetchTreatmentPlansTree());
    } else {
      yield put({
        type: 'treatmentPlan/deleteTreatmentPlanFailure',
        payload: 'Failed to delete treatment plan',
      });
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error) || 'Failed to delete treatment plan';
    yield put({
      type: 'treatmentPlan/deleteTreatmentPlanFailure',
      payload: errorMessage,
    });
  }
}

function* deleteReusableStepSaga(action: PayloadAction<{ id: number }>) {
  try {
    const response: ApiResponse<void> = yield call(
      TreatmentPlanAPI.deleteReusableStep,
      action.payload.id
    );
    if (response.success) {
      yield put({ type: 'treatmentPlan/deleteReusableStepSuccess' });
      yield put(fetchStepsTree());
    } else {
      yield put({
        type: 'treatmentPlan/deleteReusableStepFailure',
        payload: 'Failed to delete task',
      });
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error) || 'Failed to delete task';
    yield put({ type: 'treatmentPlan/deleteReusableStepFailure', payload: errorMessage });
  }
}

// Saga to save treatment plan as draft
function* saveAsDraftSaga(action: PayloadAction<{ draftName: string }>) {
  try {
    const state: RootState = yield select();
    const { currentTreatmentPlan, sentToPatientsIds } = state.treatmentPlan;
    const { draftName } = action.payload;

    // Prepare the treatment plan data for saving as draft
    const stepsToSave =
      currentTreatmentPlan.steps.length > 0 ? currentTreatmentPlan.steps : [createDefaultStep(1)];

    // Use the dedicated draft API endpoint
    const draftData: any = {
      name: draftName,
      description: '',
      steps: cleanSteps(stepsToSave),
    };

    // Include patient_id if a patient is selected
    if (sentToPatientsIds.length > 0) {
      const patientId = parseInt(sentToPatientsIds[0], 10);
      if (!isNaN(patientId)) {
        draftData.patient_id = patientId;
      }
    }

    const response: ApiResponse<TreatmentPlanResponse> = yield call(
      TreatmentPlanAPI.saveDraft,
      draftData
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to save treatment plan as draft');
    }

    // Update the current treatment plan with the draft data
    yield put(saveAsDraftSuccess(response.data));

    // Refresh treatment plans tree to show the new draft
    yield put(fetchTreatmentPlansTree());
    // Optionally could dispatch a global toast here if we had a toast system
  } catch (error: any) {
    console.error('Error saving as draft:', error);
    const errorMessage = getErrorMessage(error) || 'Failed to save as draft';
    yield put(saveAsDraftFailure(errorMessage));
  }
}

// Saga to update existing draft
function* updateDraftSaga(action: PayloadAction<{ id: number; draftName: string }>) {
  try {
    const state: RootState = yield select();
    const { currentTreatmentPlan } = state.treatmentPlan;
    const { id, draftName } = action.payload;

    // Prepare the treatment plan data for updating draft
    const stepsToSave =
      currentTreatmentPlan.steps.length > 0 ? currentTreatmentPlan.steps : [createDefaultStep(1)];

    const draftData: any = {
      name: draftName,
      description: '',
      steps: cleanSteps(stepsToSave),
    };

    const response: ApiResponse<TreatmentPlanResponse> = yield call(
      TreatmentPlanAPI.updateDraft,
      id,
      draftData
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to update draft');
    }

    // Update the current treatment plan with the updated draft data
    yield put(updateDraftSuccess(response.data));

    // Refresh treatment plans tree to reflect any changes
    yield put(fetchTreatmentPlansTree());
  } catch (error: any) {
    console.error('Error updating draft:', error);
    const errorMessage = getErrorMessage(error) || 'Failed to update draft';
    yield put(updateDraftFailure(errorMessage));
  }
}

// Root saga that combines all sagas
export function* watchTreatmentPlanActions() {
  yield takeLatest(fetchStepsTree.type, fetchStepsTreeSaga);
  yield takeLatest(createStep.type, createStepSaga);
  yield takeLatest(createFolder.type, createFolderSaga);
  yield takeLatest(fetchTreatmentPlansTree.type, fetchTreatmentPlansTreeSaga);
  yield takeLatest(createTreatmentPlan.type, createTreatmentPlanSaga);

  // New sagas for treatment plan persistence
  yield takeLatest(fetchTreatmentPlanById.type, fetchTreatmentPlanByIdSaga);
  yield takeLatest(saveTreatmentPlan.type, saveTreatmentPlanSaga);

  // New saga for sending notifications
  yield takeLatest(sendNotification.type, sendNotificationSaga);

  // New sagas for transcription
  yield takeLatest(transcribeAudioAction.type, transcribeAudioSaga);
  yield takeLatest(transcribeToPlanAction.type, transcribeToPlanSaga);

  // Delete sagas
  yield takeLatest('treatmentPlan/deleteFolder', deleteFolderSaga);
  yield takeLatest('treatmentPlan/deleteTreatmentPlan', deleteTreatmentPlanSaga);
  yield takeLatest('treatmentPlan/deleteReusableStep', deleteReusableStepSaga);

  // Save as draft saga
  yield takeLatest('treatmentPlan/saveAsDraft', saveAsDraftSaga);

  // Update draft saga
  yield takeLatest('treatmentPlan/updateDraft', updateDraftSaga);
}
